import { FC, ReactNode, useMemo } from 'react';

import classNames from 'classnames';

import Container, { ContainerProps } from '../Container';

import './ExpandableSection.scss';

import { TestMetadata } from '../types';

export interface ExpandableSectionProps {
  /** Elements in the expanded section */
  children?: ReactNode;
  /** Is section expanded or not */
  isExpanded?: boolean;
  /** This section will always be visible */
  mainSection: ReactNode;
  /** Class name */
  className?: string;
  /** Override props for container (minus children) */
  containerProps?: Omit<ContainerProps, 'children'>;
  /** Sets test id */
  testMetadata?: {
    wrapper?: TestMetadata;
    mainSection?: TestMetadata;
    expandedSection?: TestMetadata;
  };
}

const ExpandableSection: FC<ExpandableSectionProps> = ({
  children,
  className,
  isExpanded = true,
  mainSection,
  containerProps = {
    padding: 'normal',
  },
  testMetadata,
  ...otherProps
}) => {
  const expandSectionClassNames = useMemo(
    () =>
      classNames(
        'sb-expandable-section',
        `sb-expandable-section__${isExpanded ? 'expanded' : 'collapsed'}`,
        className,
      ),
    [className, isExpanded],
  );

  return (
    <Container
      {...otherProps}
      {...testMetadata?.wrapper}
      {...containerProps}
      className={expandSectionClassNames}
    >
      <div
        className="sb-expandable-section__visible"
        {...testMetadata?.mainSection}
      >
        {mainSection}
      </div>
      {isExpanded && (
        <div
          className="sb-expandable-section__children"
          {...testMetadata?.expandedSection}
        >
          {children}
        </div>
      )}
    </Container>
  );
};

export default ExpandableSection;
